import { customFontAssetId, customFontFamily } from '@vidmakr/shared';
import { api } from './api';

/**
 * Uploaded fonts, registered with the page under the family names text clips
 * use (see customFontFamily). Each loads once; a failed load (for example a
 * font that is still uploading) may be retried after a short pause.
 */
const loads = new Map<string, Promise<boolean>>();
const RETRY_MS = 10_000;
/** The face registered for each font, and the file it came from (a replaced font has a new one). */
const faces = new Map<string, FontFace>();
const versions = new Map<string, string>();

function addFace(assetId: string, source: string | ArrayBuffer): Promise<boolean> {
  // A single file serves every weight, so bold text does not fall back to another font.
  const face = new FontFace(customFontFamily(assetId), typeof source === 'string' ? `url("${source}")` : source, { weight: '1 1000' });
  const old = faces.get(assetId);
  if (old) document.fonts.delete(old);
  faces.set(assetId, face);
  // Added before loading so document.fonts reports the load (the preview redraws text then).
  document.fonts.add(face);
  return face.load().then(
    () => true,
    () => (document.fonts.delete(face), false),
  );
}

function track(assetId: string, p: Promise<boolean>): Promise<boolean> {
  loads.set(assetId, p);
  void p.then((ok) => ok || setTimeout(() => loads.get(assetId) === p && loads.delete(assetId), RETRY_MS));
  return p;
}

/** Loads an uploaded font into the page so text can be drawn with it (`version`: its storage key, to pick up a replaced file). */
export function ensureCustomFont(assetId: string, version?: string): Promise<boolean> {
  if (version !== undefined) {
    const seen = versions.get(assetId);
    versions.set(assetId, version);
    if (seen !== undefined && seen !== version) loads.delete(assetId);
  }
  const hit = loads.get(assetId);
  if (hit) return hit;
  return track(
    assetId,
    api<{ urls: Record<string, { original?: string }> }>('/api/media/urls', {
      method: 'POST',
      json: { assetIds: [assetId], variants: ['original'] },
    })
      .then((res) => res.urls[assetId]?.original)
      .then((url) => (url ? addFace(assetId, url) : false))
      .catch(() => false),
  );
}

/** Loads the uploaded font a family name refers to; built-in fonts need nothing. */
export function ensureFontFamily(family: string) {
  const id = customFontAssetId(family);
  if (id) void ensureCustomFont(id);
}

/** Registers a font from a local file right away (used while it uploads). */
export function registerLocalFont(assetId: string, data: ArrayBuffer): Promise<boolean> {
  return track(assetId, addFace(assetId, data));
}

/** Whether the browser can read the file as a font. */
export async function isValidFont(data: ArrayBuffer): Promise<boolean> {
  try {
    await new FontFace('vm-font-check', data).load();
    return true;
  } catch {
    return false;
  }
}
